import { motion } from "framer-motion";
import { ReactNode } from "react";
import "./PiSegmentedToggle.css";

export type PiSegmentedToggleOption<T extends string | number> = {
  label: ReactNode;
  testId?: string;
  ariaLabel?: string;
  title?: string;
  value: T;
};

const SEGMENT_TRANSITION = {
  type: "spring",
  stiffness: 380,
  damping: 32,
  mass: 0.8,
};

export default function PiSegmentedToggle<T extends string | number>(props: {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  /**
   * Shared-layout id for the animated active pill. Omit to render a static
   * pill — use that inside draggable or CSS-transformed containers (e.g.
   * sequence nodes), where framer-motion's layout projection re-measures
   * viewport boxes and the pill animates erratically.
   */
  layoutId?: string;
  onChange: (value: T) => void;
  options: PiSegmentedToggleOption<T>[];
  value: T;
  /**
   * Drops the per-segment minimum width and shrinks the type, for narrow
   * surfaces like the 250px properties column where the default wraps.
   */
  dense?: boolean;
  /**
   * "pill" is the original: fully round, gradient active segment with a glow.
   * "square" matches the redesigned dialogs, where every control is a soft
   * rectangle and nothing glows. Default stays pill so the actor and
   * transition editors are untouched.
   */
  variant?: "pill" | "square";
}) {
  const {
    ariaLabel,
    className = "",
    dense = false,
    disabled = false,
    fullWidth = false,
    layoutId,
    onChange,
    options,
    value,
    variant = "pill",
  } = props;

  return (
    <div
      aria-label={ariaLabel}
      className={`pi-segmented-toggle pi-segmented-toggle--${variant} ${
        dense ? "pi-segmented-toggle--dense" : ""
      } ${disabled ? "pi-segmented-toggle-disabled" : ""} ${
        fullWidth ? "pi-segmented-toggle-fullwidth" : ""
      } ${className}`.trim()}
    >
      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={String(option.value)}
            type="button"
            data-testid={option.testId}
            aria-label={option.ariaLabel}
            aria-pressed={isActive}
            title={option.title}
            className={
              "pi-segmented-toggle-button " +
              (isActive ? "pi-segmented-toggle-button-active " : "") +
              (disabled ? "pi-segmented-toggle-button-disabled" : "")
            }
            onClick={() => !disabled && onChange(option.value)}
            disabled={disabled}
          >
            {isActive ? (
              layoutId ? (
                <motion.div
                  layoutId={layoutId}
                  className="pi-segmented-toggle-pill"
                  transition={SEGMENT_TRANSITION}
                />
              ) : (
                <div className="pi-segmented-toggle-pill" />
              )
            ) : null}
            <span className="pi-segmented-toggle-label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
